import type {
  CurveDraftDocument,
  CurveKind,
  CurveTraceDraft,
  ValidationFinding,
} from "./types";

export type RequiredCurveFamily = "efficiency" | "brake_power" | "npshr";

export interface CurveGroupReadiness {
  head: CurveTraceDraft;
  missingFamilies: RequiredCurveFamily[];
}

const REQUIRED_FAMILIES: RequiredCurveFamily[] = [
  "efficiency",
  "brake_power",
  "npshr",
];

const SOURCE_CONTOUR_KINDS = new Set<CurveKind>([
  "efficiency_contour",
  "brake_power_contour",
  "npshr_contour",
]);

function contourAppliesToHead(
  contour: CurveTraceDraft,
  head: CurveTraceDraft,
): boolean {
  return (
    (contour.speed_rpm === null ||
      contour.speed_rpm === head.speed_rpm) &&
    (contour.trim_label === null ||
      contour.trim_label === head.trim_label)
  );
}

function appliesToHead(
  trace: CurveTraceDraft,
  head: CurveTraceDraft,
): boolean {
  return (
    trace.speed_rpm === head.speed_rpm &&
    (trace.trim_label === null ||
      trace.trim_label === head.trim_label)
  );
}

export function curveGroupReadiness(
  document: CurveDraftDocument,
): CurveGroupReadiness[] {
  const heads = document.traces.filter((trace) => trace.kind === "head");
  return heads.map((head) => {
    const present = new Set(
      document.traces
        .filter(
          (trace) =>
            trace.id !== head.id &&
            appliesToHead(trace, head) &&
            REQUIRED_FAMILIES.includes(trace.kind as RequiredCurveFamily),
        )
        .map((trace) => trace.kind as RequiredCurveFamily),
    );
    return {
      head,
      missingFamilies: REQUIRED_FAMILIES.filter(
        (family) => !present.has(family),
      ),
    };
  });
}

export function isContourIntersectionGap(
  finding: ValidationFinding,
): boolean {
  return finding.code.startsWith("insufficient_contour_intersections:");
}

export function pruneIncompleteCurveGroups(
  document: CurveDraftDocument,
): CurveDraftDocument {
  const readiness = curveGroupReadiness(document);
  const incompleteHeads = readiness
    .filter((group) => group.missingFamilies.length > 0)
    .map((group) => group.head);
  if (incompleteHeads.length === 0) return document;
  const retainedHeads = readiness
    .filter((group) => group.missingFamilies.length === 0)
    .map((group) => group.head);

  const shouldRemove = (trace: CurveTraceDraft): boolean => {
    if (SOURCE_CONTOUR_KINDS.has(trace.kind)) {
      return !retainedHeads.some((head) =>
        contourAppliesToHead(trace, head),
      );
    }
    if (trace.kind !== "head") {
      return !retainedHeads.some((head) => appliesToHead(trace, head));
    }
    return incompleteHeads.some((head) => trace.id === head.id);
  };
  return {
    ...document,
    traces: document.traces.filter((trace) => !shouldRemove(trace)),
  };
}

export function curveGroupLabel(head: CurveTraceDraft): string {
  if (head.trim_label !== null && head.trim_label.trim() !== "") {
    return head.trim_label;
  }
  return head.speed_rpm === null ? head.label : `${head.speed_rpm} RPM`;
}

export function curveFamilyLabel(family: RequiredCurveFamily): string {
  const labels: Record<RequiredCurveFamily, string> = {
    brake_power: "power",
    efficiency: "efficiency",
    npshr: "NPSHr",
  };
  return labels[family];
}
